<template>
  <div>
    <v-card :disabled="isProcessing" :loading="isProcessing">
      <v-tabs v-model="tab" class="stock-color" left dark>
        <v-tab href="#transport">{{ $t("STOCK.DELIVERY_NOTES.TAB_TRANSPORT") }}</v-tab>
        <v-tab href="#invoicing">{{ $t("STOCK.DELIVERY_NOTES.TAB_INVOICING") }}</v-tab>
        <v-tab href="#planning">{{ $t("STOCK.DELIVERY_NOTES.PLANNING") }}</v-tab>
        <v-spacer />
        <div class="buttons-tab d-flex align-center flex-wrap pa-2">
          <v-btn
            v-if="tab === 'transport' && selectedIds.length && hasPermissions('stock-transport-orders-create')"
            class="mr-2 primary"
            small
            @click="createTransport"
          >
            {{ $t("STOCK.DELIVERY_NOTES.CREATE_TRANSPORT") }}
          </v-btn>
          <v-btn
            v-if="tab === 'invoicing' && invoiceSelectedIds.length && hasPermissions('stock-invoices-create')"
            class="mr-2 primary"
            small
            @click="openInvoiceDialog"
          >
            {{ $t("STOCK.DELIVERY_NOTES.CREATE_INVOICE") }}
          </v-btn>
        </div>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="transport">
          <v-card flat>
            <v-card-text class="pa-0">
              <div class="dn-list-filters px-4 pt-4 pb-3">
                <div class="d-flex flex-wrap align-center">
                  <v-btn
                    small
                    class="mr-4 mb-2"
                    :color="listFilters.for_transport ? 'primary' : undefined"
                    :outlined="!listFilters.for_transport"
                    @click="listFilters.for_transport = !listFilters.for_transport"
                  >
                    <v-icon small class="mr-1">mdi-truck-delivery</v-icon>
                    {{ $t("STOCK.DELIVERY_NOTES.FOR_TRANSPORT") }}
                  </v-btn>
                  <span class="caption grey--text text--darken-1 mr-2 mb-2">{{ $t("STOCK.DELIVERY_NOTES.DIRECTION") }}</span>
                  <v-chip-group
                    v-model="listFilters.direction"
                    mandatory
                    active-class="primary--text"
                    class="dn-direction-chips mb-2"
                  >
                    <v-chip filter value="" small>{{ $t("GENERAL.LABELS.ALL") }}</v-chip>
                    <v-chip filter value="outbound" small outlined>{{ $t("STOCK.DELIVERY_NOTES.OUTBOUND") }}</v-chip>
                    <v-chip filter value="inbound" small outlined>{{ $t("STOCK.DELIVERY_NOTES.INBOUND") }}</v-chip>
                  </v-chip-group>
                </div>
                <v-row dense class="mt-1">
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="listFilters.status"
                      :items="statusItems"
                      item-text="label"
                      item-value="id"
                      :label="$t('GENERAL.LABELS.STATUS')"
                      multiple
                      chips
                      small-chips
                      deletable-chips
                      clearable
                      dense
                      outlined
                      hide-details
                      :disabled="listFilters.for_transport"
                    />
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-autocomplete
                      v-model="listFilters.company_id"
                      :items="companyOptions"
                      item-text="label"
                      item-value="id"
                      :label="$t('STOCK.DELIVERY_NOTES.COMPANY')"
                      clearable
                      dense
                      outlined
                      hide-details
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="d-flex align-center">
                    <v-btn text small color="primary" @click="clearListFilters">
                      {{ $t("STOCK.DELIVERY_NOTES.CLEAR_FILTERS") }}
                    </v-btn>
                  </v-col>
                </v-row>
                <div v-if="listFilters.for_transport" class="caption grey--text text--darken-1 mt-2">
                  {{ $t("STOCK.DELIVERY_NOTES.FOR_TRANSPORT_HINT") }}
                </div>
              </div>
              <Table
                v-if="userStatePrepared"
                ref="delivery-notes-table"
                height="500"
                name="stock-delivery-notes-table"
                :headers="headers"
                :server-data-options="{ method: tableItemsMethod }"
                :items-per-page="25"
                :loading="isProcessing"
                :filters="[]"
                :selectable="hasPermissions('stock-transport-orders-create')"
                :row-selectable="isNoteSelectable"
                dense
                fixed-header
                filterable
                @item-selected="onSelected"
              >
                <template v-for="(header, index) in headers" v-slot:[`item.${header.value}`]="{ item }">
                  <template v-if="header.value === 'code'">
                    <div :key="index">
                      <router-link :to="{ name: 'stock-delivery-notes-view', params: { id: item.item.id } }">
                        {{ item.item.code }}
                      </router-link>
                      <div v-if="item.item.paired_delivery_note" class="dn-pair">
                        <v-chip x-small outlined color="primary">
                          {{ $t("STOCK.DELIVERY_NOTES.DIRECT_TO_CUSTOMER") }}
                        </v-chip>
                        <v-chip
                          v-if="isLeadingPair(item.item)"
                          x-small
                          class="ml-1"
                          color="primary"
                          dark
                        >
                          {{ $t("STOCK.DELIVERY_NOTES.DROPSHIP_LEADING") }}
                        </v-chip>
                        <div class="caption grey--text text--darken-1">
                          <template v-if="isLeadingPair(item.item)">
                            {{ $t("STOCK.DELIVERY_NOTES.DROPSHIP_LIST_LEADING") }}
                            ·
                          </template>
                          <template v-else>
                            {{ $t("STOCK.DELIVERY_NOTES.DROPSHIP_LIST_COMMERCIAL") }}
                          </template>
                          <router-link
                            :to="{ name: 'stock-delivery-notes-view', params: { id: item.item.paired_delivery_note.id } }"
                          >
                            {{ item.item.paired_delivery_note.code }}
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </template>
                  <template v-else-if="header.value === 'status'">
                    <v-chip :key="index" small :color="statusColor(item.item.status)" dark>
                      {{ statusLabel(item.item.status) }}
                    </v-chip>
                  </template>
                  <template v-else-if="header.value === 'direction'">
                    <span :key="index">{{ directionLabel(item.item.direction) }}</span>
                  </template>
                  <template v-else-if="header.value === 'company.name'">
                    <span :key="index">{{ item.item.company?.label || item.item.company?.name || "-" }}</span>
                  </template>
                  <template v-else-if="header.value === 'order_code'">
                    <span :key="index">{{ item.item.order_code || item.item.sales_order?.code || item.item.purchase_order?.code || "-" }}</span>
                  </template>
                  <template v-else-if="header.value === 'transport_order.code'">
                    <router-link
                      v-if="item.item.transport_order && item.item.transport_order.id"
                      :key="index"
                      :to="{ name: 'stock-transport-orders-view', params: { id: item.item.transport_order.id } }"
                    >
                      <v-icon x-small class="mr-1">mdi-truck</v-icon>
                      {{ item.item.transport_order.code }}
                    </router-link>
                    <span v-else :key="`free-${index}`" class="grey--text">{{ $t("STOCK.DELIVERY_NOTES.NOT_IN_TRANSPORT") }}</span>
                  </template>
                  <template v-else>
                    <span :key="index">{{ item.value || "-" }}</span>
                  </template>
                </template>
              </Table>
            </v-card-text>
          </v-card>
        </v-tab-item>
        <v-tab-item value="invoicing">
          <v-card flat>
            <v-card-text class="pa-0">
              <div class="dn-list-filters px-4 pt-4 pb-3">
                <div class="d-flex flex-wrap align-center">
                  <span class="caption grey--text text--darken-1 mr-2 mb-2">{{ $t("STOCK.DELIVERY_NOTES.DIRECTION") }}</span>
                  <v-chip-group
                    v-model="invoiceFilters.direction"
                    mandatory
                    active-class="primary--text"
                    class="dn-direction-chips mb-2"
                  >
                    <v-chip filter value="" small>{{ $t("GENERAL.LABELS.ALL") }}</v-chip>
                    <v-chip filter value="outbound" small outlined>{{ $t("STOCK.DELIVERY_NOTES.OUTBOUND") }}</v-chip>
                    <v-chip filter value="inbound" small outlined>{{ $t("STOCK.DELIVERY_NOTES.INBOUND") }}</v-chip>
                  </v-chip-group>
                </div>
                <v-row dense class="mt-1">
                  <v-col cols="12" md="5">
                    <v-autocomplete
                      v-model="invoiceFilters.company_id"
                      :items="companyOptions"
                      item-text="label"
                      item-value="id"
                      :label="$t('STOCK.DELIVERY_NOTES.COMPANY')"
                      clearable
                      dense
                      outlined
                      hide-details
                    />
                  </v-col>
                </v-row>
                <div class="caption grey--text text--darken-1 mt-2">
                  {{ $t("STOCK.DELIVERY_NOTES.FOR_INVOICE_HINT") }}
                </div>
              </div>
              <Table
                v-if="invoiceTableReady"
                ref="delivery-notes-invoice-table"
                height="500"
                name="stock-delivery-notes-invoice-table"
                :headers="invoiceHeaders"
                :server-data-options="{ method: tableItemsMethod }"
                :items-per-page="25"
                :loading="isProcessing"
                :filters="[]"
                :selectable="hasPermissions('stock-invoices-create')"
                dense
                fixed-header
                filterable
                @item-selected="onInvoiceSelected"
              >
                <template v-for="(header, index) in invoiceHeaders" v-slot:[`item.${header.value}`]="{ item }">
                  <template v-if="header.value === 'code'">
                    <div :key="index">
                      <router-link :to="{ name: 'stock-delivery-notes-view', params: { id: item.item.id } }">
                        {{ item.item.code }}
                      </router-link>
                      <div v-if="item.item.paired_delivery_note" class="dn-pair">
                        <v-chip x-small outlined color="primary">
                          {{ $t("STOCK.DELIVERY_NOTES.DIRECT_TO_CUSTOMER") }}
                        </v-chip>
                        <v-chip
                          v-if="isLeadingPair(item.item)"
                          x-small
                          class="ml-1"
                          color="primary"
                          dark
                        >
                          {{ $t("STOCK.DELIVERY_NOTES.DROPSHIP_LEADING") }}
                        </v-chip>
                        <div class="caption grey--text text--darken-1">
                          <template v-if="isLeadingPair(item.item)">
                            {{ $t("STOCK.DELIVERY_NOTES.DROPSHIP_LIST_LEADING") }}
                            ·
                          </template>
                          <template v-else>
                            {{ $t("STOCK.DELIVERY_NOTES.DROPSHIP_LIST_COMMERCIAL") }}
                          </template>
                          <router-link
                            :to="{ name: 'stock-delivery-notes-view', params: { id: item.item.paired_delivery_note.id } }"
                          >
                            {{ item.item.paired_delivery_note.code }}
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </template>
                  <template v-else-if="header.value === 'status'">
                    <v-chip :key="index" small :color="statusColor(item.item.status)" dark>
                      {{ statusLabel(item.item.status) }}
                    </v-chip>
                  </template>
                  <template v-else-if="header.value === 'direction'">
                    <span :key="index">{{ directionLabel(item.item.direction) }}</span>
                  </template>
                  <template v-else-if="header.value === 'company.name'">
                    <span :key="index">{{ item.item.company?.label || item.item.company?.name || "-" }}</span>
                  </template>
                  <template v-else-if="header.value === 'order_code'">
                    <span :key="index">{{ item.item.order_code || item.item.sales_order?.code || item.item.purchase_order?.code || "-" }}</span>
                  </template>
                  <template v-else>
                    <span :key="index">{{ item.value || "-" }}</span>
                  </template>
                </template>
              </Table>
            </v-card-text>
          </v-card>
        </v-tab-item>
        <v-tab-item value="planning">
          <v-card flat>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="2">
                  <v-select
                    v-model="planning.direction"
                    :items="directionItems"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.DELIVERY_NOTES.DIRECTION')"
                  />
                </v-col>
                <v-col cols="12" md="2">
                  <DatePicker v-model="planning.date_from" :label="$t('STOCK.DELIVERY_NOTES.DATE_FROM')" without-hint clearable />
                </v-col>
                <v-col cols="12" md="2">
                  <DatePicker v-model="planning.date_to" :label="$t('STOCK.DELIVERY_NOTES.DATE_TO')" without-hint clearable />
                </v-col>
                <v-col cols="12" md="3">
                  <v-autocomplete
                    v-model="planning.company_id"
                    :items="companyOptions"
                    :search-input.sync="companySearch"
                    item-text="label"
                    item-value="id"
                    :label="$t('STOCK.DELIVERY_NOTES.COMPANY')"
                    clearable
                  />
                </v-col>
                <v-col cols="12" md="3" class="d-flex align-center">
                  <v-checkbox v-model="planning.only_unplanned" :label="$t('STOCK.DELIVERY_NOTES.ONLY_UNPLANNED')" hide-details class="mr-4" />
                  <v-btn color="primary" :loading="isProcessing" @click="loadPlanning">
                    {{ $t("STOCK.DELIVERY_NOTES.LOAD_PLANNING") }}
                  </v-btn>
                </v-col>
              </v-row>
              <v-text-field v-model="planning.note" :label="$t('STOCK.DELIVERY_NOTES.NOTE')" class="mt-2" />

              <v-alert v-if="!planningRows.length" type="info" dense text class="mt-4">
                {{ $t("STOCK.DELIVERY_NOTES.NO_PLANNING") }}
              </v-alert>

              <div v-for="order in planningRows" :key="order.id" class="mt-4">
                <div class="d-flex align-center mb-1">
                  <v-checkbox
                    :input-value="isOrderSelected(order)"
                    hide-details
                    class="mt-0 pt-0"
                    @change="toggleOrder(order, $event)"
                  />
                  <strong class="ml-2">{{ order.code }}</strong>
                  <span class="ml-2 caption">{{ order.company?.name }} · {{ order.warehouse?.name }}</span>
                </div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th></th>
                      <th>{{ $t("STOCK.PRODUCTS.PRODUCT") }}</th>
                      <th>{{ $t("STOCK.DELIVERY_NOTES.QTY") }}</th>
                      <th>{{ $t("STOCK.DELIVERY_NOTES.ETD") }}</th>
                      <th>{{ $t("STOCK.DELIVERY_NOTES.ETA") }}</th>
                      <th v-if="planning.direction === 'inbound'">{{ $t("STOCK.DELIVERY_NOTES.UNLOAD_AT") }}</th>
                      <th v-if="planning.direction === 'outbound'">{{ $t("STOCK.DELIVERY_NOTES.LOAD_FROM") }}</th>
                      <th>{{ $t("STOCK.DELIVERY_NOTES.NOTE") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="line in order.lines" :key="line.schedule_id">
                      <td>
                        <v-checkbox v-model="selectedScheduleIds" :value="line.schedule_id" hide-details class="mt-0 pt-0" />
                      </td>
                      <td>{{ line.product_code }} · {{ line.product_name }}</td>
                      <td>{{ formatQty(line.quantity) }} {{ line.unit }}</td>
                      <td>{{ line.etd_date || "-" }}</td>
                      <td>{{ line.eta_date || "-" }}</td>
                      <td v-if="planning.direction === 'inbound'">{{ line.destination_warehouse?.name || "-" }}</td>
                      <td v-if="planning.direction === 'outbound'">{{ line.source_warehouse?.name || "-" }}</td>
                      <td>{{ line.note || "-" }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </div>

              <v-btn
                v-if="selectedScheduleIds.length"
                class="mt-4"
                color="success"
                :loading="isProcessing"
                @click="createNotes"
              >
                {{ $t("STOCK.DELIVERY_NOTES.CREATE_FROM_SELECTION") }} ({{ selectedScheduleIds.length }})
              </v-btn>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <StockInvoiceLinkDialog
      ref="invoiceDialog"
      v-model="showInvoiceDialog"
      :delivery-note-ids="invoiceSelectedIds"
      kind="delivery"
      :title="$t('STOCK.DELIVERY_NOTES.CREATE_INVOICE')"
      :hint="$t('STOCK.DELIVERY_NOTES.BULK_INVOICE_HINT', { count: invoiceSelectedIds.length })"
      :show-pass-through="invoiceSelectionOutbound"
      :submitting="isProcessing"
      @confirm="handleInvoiceConfirm"
      @error="handleInvoiceError"
    />
  </div>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import DatePicker from "@/views/partials/form/DatePicker.vue";
import StockInvoiceLinkDialog from "@/views/partials/stock/invoices/StockInvoiceLinkDialog.vue";
import userStateService from "@/common/userState.service";
import {
  GET_STOCK_DELIVERY_NOTES,
  GET_STOCK_DELIVERY_NOTE_PLANNING,
  CREATE_STOCK_DELIVERY_NOTES,
  CREATE_TRANSPORT_ORDER_FROM_DELIVERY_NOTES,
  CREATE_STOCK_INVOICE_FROM_DELIVERY_NOTES,
} from "@/store/stock/stockDeliveryNotes.module";
import { GET_COMPANIES_OPTIONS } from "@/store/companies.module";
import { SHOW_SNACK } from "@/store/snack.module";
import { formatStockQuantity } from "@/common/stock/stockNumberFormat";

const LIST_FILTERS_KEY = "table-state.stock-delivery-notes-table.serverFilters";
const INVOICE_FILTERS_KEY = "table-state.stock-delivery-notes-invoice-table.serverFilters";

function readListFilters() {
  const stored = userStateService.getState(LIST_FILTERS_KEY, null);
  const source = stored === null || stored === undefined ? { for_transport: true } : stored;
  const direction = Array.isArray(source.direction)
    ? (source.direction.length === 1 ? source.direction[0] : "")
    : (source.direction || "");
  const status = Array.isArray(source.status) ? source.status : (source.status ? [source.status] : []);

  return {
    for_transport: !!source.for_transport,
    direction,
    status,
    company_id: source.company_id ? Number(source.company_id) : null,
  };
}

function toServerFilters(listFilters) {
  const filters = {};
  if (listFilters.for_transport) {
    filters.for_transport = true;
  } else if (listFilters.status && listFilters.status.length) {
    filters.status = listFilters.status;
  }
  if (listFilters.direction === "outbound" || listFilters.direction === "inbound") {
    filters.direction = listFilters.direction;
  }
  if (listFilters.company_id) {
    filters.company_id = listFilters.company_id;
  }
  return filters;
}

function invoiceServerFilters(invoiceFilters) {
  const filters = { for_invoice: true };
  if (invoiceFilters.direction === "outbound" || invoiceFilters.direction === "inbound") {
    filters.direction = invoiceFilters.direction;
  }
  if (invoiceFilters.company_id) {
    filters.company_id = invoiceFilters.company_id;
  }
  return filters;
}

export default {
  name: "StockListDeliveryNotes",
  components: { Table, DatePicker, StockInvoiceLinkDialog },
  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      tab: this.$route.query.planning ? "planning" : "transport",
      isProcessing: false,
      userStatePrepared: false,
      listFiltersReady: false,
      tableItemsMethod: GET_STOCK_DELIVERY_NOTES,
      selectedIds: [],
      invoiceSelectedIds: [],
      invoiceSelection: null,
      showInvoiceDialog: false,
      invoiceTableReady: false,
      invoiceFiltersReady: false,
      invoiceFilters: { direction: "", company_id: null },
      listFilters: readListFilters(),
      companyOptions: [],
      companySearch: null,
      planningRows: [],
      selectedScheduleIds: [],
      planning: {
        direction: this.$route.query.direction || "outbound",
        date_from: this.$route.query.date_from || today,
        date_to: this.$route.query.date_to || today,
        company_id: this.$route.query.company_id ? Number(this.$route.query.company_id) : null,
        only_unplanned: true,
        note: "",
        order_ids: this.$route.query.order_id ? [Number(this.$route.query.order_id)] : [],
      },
      headers: [
        { text: this.$t("STOCK.DELIVERY_NOTES.DOCUMENT_CODE"), value: "code", sortable: true },
        { text: this.$t("GENERAL.LABELS.STATUS"), value: "status", sortable: true },
        { text: this.$t("STOCK.DELIVERY_NOTES.DIRECTION"), value: "direction", sortable: true },
        { text: this.$t("STOCK.DELIVERY_NOTES.COMPANY"), value: "company.name", sortable: false },
        { text: this.$t("STOCK.DELIVERY_NOTES.ORDER"), value: "order_code", sortable: false },
        { text: this.$t("STOCK.DELIVERY_NOTES.ETD"), value: "etd_date", sortable: true },
        { text: this.$t("STOCK.DELIVERY_NOTES.ETA"), value: "eta_date", sortable: true },
        { text: this.$t("STOCK.DELIVERY_NOTES.TRANSPORT"), value: "transport_order.code", sortable: false },
      ],
      invoiceHeaders: [
        { text: this.$t("STOCK.DELIVERY_NOTES.DOCUMENT_CODE"), value: "code", sortable: true },
        { text: this.$t("GENERAL.LABELS.STATUS"), value: "status", sortable: true },
        { text: this.$t("STOCK.DELIVERY_NOTES.DIRECTION"), value: "direction", sortable: true },
        { text: this.$t("STOCK.DELIVERY_NOTES.COMPANY"), value: "company.name", sortable: false },
        { text: this.$t("STOCK.DELIVERY_NOTES.ORDER"), value: "order_code", sortable: false },
        { text: this.$t("STOCK.DELIVERY_NOTES.ETD"), value: "etd_date", sortable: true },
        { text: this.$t("STOCK.DELIVERY_NOTES.ETA"), value: "eta_date", sortable: true },
      ],
    };
  },
  computed: {
    directionItems() {
      return [
        { id: "outbound", label: this.$t("STOCK.DELIVERY_NOTES.OUTBOUND") },
        { id: "inbound", label: this.$t("STOCK.DELIVERY_NOTES.INBOUND") },
      ];
    },
    statusItems() {
      return ["draft", "confirmed", "executed", "invoiced", "cancelled"].map((status) => ({
        id: status,
        label: this.statusLabel(status),
      }));
    },
    invoiceSelectionOutbound() {
      return this.invoiceSelection?.direction === "outbound";
    },
  },
  watch: {
    tab(value) {
      if (value === "invoicing") {
        this.prepareInvoiceTable();
      }
      if (value === "planning") {
        this.loadPlanning();
      }
    },
    listFilters: {
      deep: true,
      handler() {
        if (!this.listFiltersReady) {
          return;
        }
        this.applyListFilters();
      },
    },
    invoiceFilters: {
      deep: true,
      handler() {
        if (!this.invoiceFiltersReady) {
          return;
        }
        this.applyInvoiceFilters();
      },
    },
  },
  mounted() {
    userStateService.getState("table-state.stock-delivery-notes-table.blinkRowItemKey", null);
    userStateService.setState(LIST_FILTERS_KEY, toServerFilters(this.listFilters));
    this.userStatePrepared = true;
    this.$store.dispatch(GET_COMPANIES_OPTIONS, {}).then((response) => {
      this.companyOptions = response.data || response || [];
    });
    if (this.tab === "planning") {
      this.loadPlanning();
    }
    this.$nextTick(() => {
      this.listFiltersReady = true;
    });
  },
  methods: {
    formatQty(value) {
      return formatStockQuantity(value);
    },
    isNoteSelectable(item) {
      if (!item || item.needs_own_transport === false || item.is_intercompany_mirror || item.transport_source_delivery_note_id) {
        return false;
      }
      return !item.stock_transport_order_id;
    },
    applyListFilters() {
      const table = this.$refs["delivery-notes-table"];
      if (!table) {
        return;
      }
      this.selectedIds = [];
      table.serverHandleFiltersChange(toServerFilters(this.listFilters), table.options);
    },
    clearListFilters() {
      this.listFiltersReady = false;
      this.listFilters = {
        for_transport: false,
        direction: "",
        status: [],
        company_id: null,
      };
      this.$nextTick(() => {
        this.listFiltersReady = true;
        this.applyListFilters();
      });
    },
    onSelected(item) {
      if (!this.isNoteSelectable(item)) {
        return;
      }
      const id = Number(item && (item.id || item.item?.id));
      if (!id) {
        return;
      }
      const isSelected = this.selectedIds.includes(id);
      if (isSelected) {
        this.selectedIds = this.selectedIds.filter((value) => value !== id);
        this.$set(item, "selected", false);
        return;
      }
      this.selectedIds = [...this.selectedIds, id];
      this.$set(item, "selected", true);
    },
    statusColor(status) {
      return { draft: "grey", confirmed: "primary", executed: "success", invoiced: "blue", cancelled: "error" }[status] || "grey";
    },
    statusLabel(status) {
      const key = `STOCK.DELIVERY_NOTES.STATUS_${(status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : status;
    },
    isLeadingPair(note) {
      return note?.dropship_pair_role === "leading"
        || (note?.paired_delivery_note && note.direction === "outbound");
    },
    directionLabel(direction) {
      return direction === "inbound"
        ? this.$t("STOCK.DELIVERY_NOTES.INBOUND")
        : this.$t("STOCK.DELIVERY_NOTES.OUTBOUND");
    },
    async loadPlanning() {
      this.isProcessing = true;
      try {
        this.planningRows = await this.$store.dispatch(GET_STOCK_DELIVERY_NOTE_PLANNING, {
          direction: this.planning.direction,
          date_from: this.planning.date_from,
          date_to: this.planning.date_to,
          company_id: this.planning.company_id,
          only_unplanned: this.planning.only_unplanned,
          order_ids: this.planning.order_ids,
        });
        this.selectedScheduleIds = [];
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: errors?.message || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    isOrderSelected(order) {
      const ids = (order.lines || []).map((line) => line.schedule_id);
      return ids.length > 0 && ids.every((id) => this.selectedScheduleIds.includes(id));
    },
    toggleOrder(order, selected) {
      const ids = (order.lines || []).map((line) => line.schedule_id);
      if (selected) {
        this.selectedScheduleIds = Array.from(new Set([...this.selectedScheduleIds, ...ids]));
      } else {
        this.selectedScheduleIds = this.selectedScheduleIds.filter((id) => !ids.includes(id));
      }
    },
    async createNotes() {
      this.isProcessing = true;
      try {
        const notes = await this.$store.dispatch(CREATE_STOCK_DELIVERY_NOTES, {
          direction: this.planning.direction,
          lines: this.selectedScheduleIds.map((schedule_id) => ({ schedule_id })),
          etd_date: this.planning.direction === "outbound" ? this.planning.date_from : null,
          eta_date: this.planning.direction === "inbound" ? this.planning.date_from : null,
          note: this.planning.note || null,
          confirm: true,
        });
        this.$store.dispatch(SHOW_SNACK, { message: this.$t("GENERAL.SUCCESS_MESSAGE"), color: "success" });
        const first = Array.isArray(notes) ? notes[0] : notes;
        if (first?.id) {
          this.$router.push({ name: "stock-delivery-notes-view", params: { id: first.id } });
        } else {
          this.tab = "list";
          this.$refs["delivery-notes-table"]?.fetchServerData();
        }
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: errors?.message || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
    async createTransport() {
      try {
        const order = await this.$store.dispatch(CREATE_TRANSPORT_ORDER_FROM_DELIVERY_NOTES, {
          delivery_note_ids: this.selectedIds,
        });
        this.$router.push({ name: "stock-transport-orders-view", params: { id: order.id } });
      } catch (errors) {
        this.$store.dispatch(SHOW_SNACK, {
          message: errors?.message || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      }
    },
    prepareInvoiceTable() {
      if (this.invoiceTableReady) {
        return;
      }
      userStateService.setState(INVOICE_FILTERS_KEY, invoiceServerFilters(this.invoiceFilters));
      this.invoiceTableReady = true;
      this.$nextTick(() => {
        this.invoiceFiltersReady = true;
      });
    },
    applyInvoiceFilters() {
      const table = this.$refs["delivery-notes-invoice-table"];
      if (!table) {
        return;
      }
      this.invoiceSelectedIds = [];
      this.invoiceSelection = null;
      table.serverHandleFiltersChange(invoiceServerFilters(this.invoiceFilters), table.options);
    },
    noteFromSelection(item) {
      return item?.item || item;
    },
    onInvoiceSelected(item) {
      const note = this.noteFromSelection(item);
      const id = Number(note && note.id);
      if (!id) {
        return;
      }
      const isSelected = this.invoiceSelectedIds.includes(id);
      if (!isSelected) {
        if (
          this.invoiceSelection
          && (this.invoiceSelection.company_id !== note.company_id
            || this.invoiceSelection.direction !== note.direction)
        ) {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("STOCK.DELIVERY_NOTES.INVOICE_MIXED_SELECTION"),
            color: "warning",
          });
          return;
        }
        this.invoiceSelection = {
          company_id: note.company_id,
          direction: note.direction,
        };
        this.invoiceSelectedIds = [...this.invoiceSelectedIds, id];
        this.$set(item, "selected", true);
        return;
      }
      this.invoiceSelectedIds = this.invoiceSelectedIds.filter((value) => value !== id);
      this.$set(item, "selected", false);
      if (!this.invoiceSelectedIds.length) {
        this.invoiceSelection = null;
      }
    },
    openInvoiceDialog() {
      if (!this.invoiceSelectedIds.length) {
        return;
      }
      this.showInvoiceDialog = true;
    },
    handleInvoiceError(errors) {
      this.$store.dispatch(SHOW_SNACK, {
        message: errors?.message || this.$t("GENERAL.UNEXPECTED_ERROR"),
        color: "error",
      });
    },
    handleInvoiceConfirm(options) {
      if (options.openOnly && options.stock_invoice_id) {
        this.showInvoiceDialog = false;
        this.$router.push({
          name: "stock-invoices-view",
          params: { id: options.stock_invoice_id },
        });
        return;
      }
      this.createInvoice(options);
    },
    async createInvoice(options = {}) {
      this.isProcessing = true;
      try {
        const invoice = await this.$store.dispatch(CREATE_STOCK_INVOICE_FROM_DELIVERY_NOTES, {
          delivery_note_ids: this.invoiceSelectedIds,
          stock_invoice_id: options.stock_invoice_id,
          pass_through_extra_costs: options.pass_through_extra_costs || false,
          external_number: options.external_number,
        });
        this.showInvoiceDialog = false;
        this.$router.push({ name: "stock-invoices-view", params: { id: invoice.id } });
      } catch (errors) {
        if (this.$refs.invoiceDialog && this.$refs.invoiceDialog.applySubmitError(errors)) {
          return;
        }
        this.$store.dispatch(SHOW_SNACK, {
          message: errors?.message || this.$t("GENERAL.UNEXPECTED_ERROR"),
          color: "error",
        });
      } finally {
        this.isProcessing = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.dn-list-filters {
  background: #f6f8fb;
  border-bottom: 1px solid #e4e8ee;
}

.dn-direction-chips {
  flex: 0 1 auto;
}

.dn-pair {
  margin-top: 2px;
  line-height: 1.3;
}
</style>
